import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  CardQuotaRequestDetail,
  CardQuotaRequestTableDto,
  SaveCardQuotaRequestPayload,
  SaveCardQuotaRequestResponse,
} from '../../../models/card-quota/card-quota-request.model';
import {
  ClientQuotaDetail,
  ClientQuotaTableDto,
  AllocateQuotaPayload,
  AllocateQuotaResponse,
  QuotaAllocationDetail,
  QuotaAllocationRequestDetail,
  QuotaAllocationRequestListRow,
  QuotaAllocationRequestTableDto,
  QuotaAllocationTableDto,
} from '../../../models/card-quota/client-quota.model';
import {
  SaveSiteQuotaConfigPayload,
  SiteQuotaConfig,
} from '../../../models/card-quota/site-quota-config.model';
import {
  CardTypeConfigDetail,
  CardTypeConfigTableDto,
  PlatformManagedCardType,
  SaveCardTypeConfigPayload,
} from '../../../models/card-quota/card-type-config.model';
import {
  IssuePlatformParkingCardPayload,
  IssuePlatformParkingCardResponse,
  PlatformCardTableDto,
  RevokePlatformParkingCardPayload,
} from '../../../models/card-quota/platform-card.model';
import {
  QuotaCardAdvanceDetail,
  QuotaCardAdvanceTableDto,
  QuotaCardAdvanceActionPayload,
  SaveQuotaCardAdvancePayload,
  SaveQuotaCardAdvanceResponse,
  AdvanceVehicleTypeOption,
  AdvanceQuotaOption,
  AdvanceQuotaOptionTableDto,
  QuotaCardAdvanceActivateCode,
} from '../../../models/card-quota/quota-card-advance.model';
import {
  CardAdvanceTableDto,
  CardAdvanceDetail,
  StandardAdvanceCardConfig,
  StandardAdvanceConfigOption,
  FinanceForCardAdvanceSearch,
  SaveCardAdvancePayload,
  SaveCardAdvanceResponse,
  CardAdvanceActionPayload,
} from '../../../models/card-quota/card-advance.model';
import { AccessibleClientsForUserDto } from '../../../models/card-quota/user-client-access.model';

@Injectable({ providedIn: 'root' })
export class ApiCardQuotaService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  private currentUserId(): number {
    return Number(this.cookieService.get('user_id') || 0);
  }

  getCardQuotaRequestsForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    clientId?: number | null,
    status?: string | null,
    requestType?: string | null,
    dateFrom?: string | null,
    dateTo?: string | null,
    searchField?: string | null,
    searchText?: string | null
  ): Observable<CardQuotaRequestTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      page,
      pageSize,
    };
    if (clientId != null) params['clientId'] = clientId;
    if (status) params['status'] = status;
    if (requestType) params['requestType'] = requestType;
    if (dateFrom) params['dateFrom'] = dateFrom;
    if (dateTo) params['dateTo'] = dateTo;
    if (searchField && searchText?.trim()) {
      params['searchField'] = searchField;
      params['searchText'] = searchText.trim();
    }
    return this.apiMethodService.request<CardQuotaRequestTableDto>(
      '/CardQuota/getCardQuotaRequestsForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getCardQuotaRequestsForTable', token, headerOnly: false, params }
    );
  }

  getCardQuotaRequestById(id: number): Observable<CardQuotaRequestDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<CardQuotaRequestDetail>(
      '/CardQuota/getCardQuotaRequestById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getCardQuotaRequestById', token, headerOnly: false, params: { id, userId: this.currentUserId() } }
    );
  }

  saveCardQuotaRequest(payload: SaveCardQuotaRequestPayload): Observable<SaveCardQuotaRequestResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<SaveCardQuotaRequestResponse>(
      '/CardQuota/saveCardQuotaRequest',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'saveCardQuotaRequest', token, headerOnly: false, body: payload }
    );
  }

  getClientQuotasForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    clientId?: number | null,
    quotaLevel?: string | null,
    quotaStatus?: string | null,
    searchField?: string | null,
    searchText?: string | null
  ): Observable<ClientQuotaTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      page,
      pageSize,
    };
    if (clientId != null) params['clientId'] = clientId;
    if (quotaLevel) params['quotaLevel'] = quotaLevel;
    if (quotaStatus) params['quotaStatus'] = quotaStatus;
    if (searchField && searchText?.trim()) {
      params['searchField'] = searchField;
      params['searchText'] = searchText.trim();
    }
    return this.apiMethodService.request<ClientQuotaTableDto>(
      '/CardQuota/getClientQuotasForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getClientQuotasForTable', token, headerOnly: false, params }
    );
  }

  getClientQuotaById(id: number): Observable<ClientQuotaDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ClientQuotaDetail>(
      '/CardQuota/getClientQuotaById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getClientQuotaById', token, headerOnly: false, params: { id, userId: this.currentUserId() } }
    );
  }

  getQuotaAllocationsForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    masterQuotaId?: number | null
  ): Observable<QuotaAllocationTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId, page, pageSize };
    if (masterQuotaId != null && masterQuotaId > 0) params['masterQuotaId'] = masterQuotaId;
    return this.apiMethodService.request<QuotaAllocationTableDto>(
      '/CardQuota/getQuotaAllocationsForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getQuotaAllocationsForTable', token, headerOnly: false, params }
    );
  }

  getQuotaAllocationRequestById(id: number): Observable<QuotaAllocationRequestDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<QuotaAllocationRequestDetail>(
      '/CardQuota/getQuotaAllocationRequestById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getQuotaAllocationRequestById', token, headerOnly: false, params: { id } }
    );
  }

  getQuotaAllocationRequestsForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    masterQuotaId?: number | null,
    status?: string | null
  ): Observable<QuotaAllocationRequestTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      page,
      pageSize,
    };
    if (masterQuotaId != null && masterQuotaId > 0) params['masterQuotaId'] = masterQuotaId;
    if (status) params['status'] = status;
    return this.apiMethodService.request<QuotaAllocationRequestTableDto>(
      '/CardQuota/getQuotaAllocationRequestsForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getQuotaAllocationRequestsForTable', token, headerOnly: false, params }
    );
  }

  getQuotaAllocationById(id: number): Observable<QuotaAllocationDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<QuotaAllocationDetail>(
      '/CardQuota/getQuotaAllocationById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getQuotaAllocationById', token, headerOnly: false, params: { id } }
    );
  }

  allocateQuotaToTenant(payload: AllocateQuotaPayload): Observable<AllocateQuotaResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AllocateQuotaResponse>(
      '/CardQuota/allocateQuotaToTenant',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'allocateQuotaToTenant', token, headerOnly: false, body: payload }
    );
  }

  getSiteQuotaConfig(siteId: number): Observable<SiteQuotaConfig | null> {
    const token = this.cookieService.get('token');
    return this.apiMethodService
      .request<SiteQuotaConfig | null>('/CardQuota/getSiteQuotaConfig', 'GET', this.baseUrl, {
        isConsoleLog: true,
        labelLog: 'getSiteQuotaConfig',
        token,
        headerOnly: false,
        params: { siteId },
      })
      .pipe(catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err))));
  }

  saveSiteQuotaConfig(payload: SaveSiteQuotaConfigPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/saveSiteQuotaConfig',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'saveSiteQuotaConfig', token, headerOnly: false, body: payload }
    );
  }

  getCardTypeConfigsForTable(
    page = 1,
    pageSize = 20,
    siteId?: number | null,
    enable?: boolean | null
  ): Observable<CardTypeConfigTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number | boolean> = { page, pageSize };
    if (siteId != null && siteId > 0) params['siteId'] = siteId;
    if (enable != null) params['enable'] = enable;
    return this.apiMethodService.request<CardTypeConfigTableDto>(
      '/CardQuota/getCardTypeConfigsForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getCardTypeConfigsForTable', token, headerOnly: false, params }
    );
  }

  getCardTypeConfigById(id: number): Observable<CardTypeConfigDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<CardTypeConfigDetail>(
      '/CardQuota/getCardTypeConfigById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getCardTypeConfigById', token, headerOnly: false, params: { id } }
    );
  }

  getPlatformManagedCardTypes(
    siteId?: number | null,
    cardScope?: string | null
  ): Observable<PlatformManagedCardType[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {};
    if (siteId != null && siteId > 0) params['siteId'] = siteId;
    if (cardScope) params['cardScope'] = cardScope;
    return this.apiMethodService.request<PlatformManagedCardType[]>(
      '/CardQuota/getPlatformManagedCardTypes',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getPlatformManagedCardTypes', token, headerOnly: false, params }
    );
  }

  saveCardTypeConfig(payload: SaveCardTypeConfigPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/saveCardTypeConfig',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'saveCardTypeConfig', token, headerOnly: false, body: payload }
    );
  }

  deleteCardTypeConfig(id: number, userId: number, menuId: number): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/deleteCardTypeConfig',
      'DELETE',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteCardTypeConfig',
        token,
        headerOnly: false,
        params: { id, userId, menuId },
      }
    );
  }

  getPlatformCardsForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    clientId?: number | null,
    platformCardStatus?: string | null,
    searchField?: string | null,
    searchText?: string | null
  ): Observable<PlatformCardTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      page,
      pageSize,
    };
    if (clientId != null) params['clientId'] = clientId;
    if (platformCardStatus) params['platformCardStatus'] = platformCardStatus;
    if (searchField && searchText?.trim()) {
      params['searchField'] = searchField;
      params['searchText'] = searchText.trim();
    }
    return this.apiMethodService.request<PlatformCardTableDto>(
      '/CardQuota/getPlatformCardsForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getPlatformCardsForTable', token, headerOnly: false, params }
    );
  }

  getUserClientAccessScope(siteId: number, userId: number): Observable<AccessibleClientsForUserDto['scope']> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AccessibleClientsForUserDto['scope']>(
      '/CardQuota/getUserClientAccessScope',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getUserClientAccessScope', token, headerOnly: false, params: { siteId, userId } }
    );
  }

  getAccessibleClientsForUser(siteId: number, userId: number): Observable<AccessibleClientsForUserDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AccessibleClientsForUserDto>(
      '/CardQuota/getAccessibleClientsForUser',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getAccessibleClientsForUser', token, headerOnly: false, params: { siteId, userId } }
    );
  }

  issuePlatformParkingCard(payload: IssuePlatformParkingCardPayload): Observable<IssuePlatformParkingCardResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<IssuePlatformParkingCardResponse>(
      '/CardQuota/issuePlatformParkingCard',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'issuePlatformParkingCard', token, headerOnly: false, body: payload }
    );
  }

  revokePlatformParkingCard(payload: RevokePlatformParkingCardPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/revokePlatformParkingCard',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'revokePlatformParkingCard', token, headerOnly: false, body: payload }
    );
  }

  getQuotaCardAdvancesForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    clientId?: number | null,
    status?: number | null,
    dateFrom?: string | null,
    dateTo?: string | null
  ): Observable<QuotaCardAdvanceTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      page,
      pageSize,
    };
    if (clientId != null) params['clientId'] = clientId;
    if (status != null) params['status'] = status;
    if (dateFrom) params['dateFrom'] = dateFrom;
    if (dateTo) params['dateTo'] = dateTo;
    return this.apiMethodService.request<QuotaCardAdvanceTableDto>(
      '/CardQuota/getQuotaCardAdvancesForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getQuotaCardAdvancesForTable', token, headerOnly: false, params }
    );
  }

  getQuotaCardAdvanceById(id: number): Observable<QuotaCardAdvanceDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<QuotaCardAdvanceDetail>(
      '/CardQuota/getQuotaCardAdvanceById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getQuotaCardAdvanceById', token, headerOnly: false, params: { id, userId: this.currentUserId() } }
    );
  }

  getAdvanceQuotasSummaryForClient(siteId: number, clientId: number): Observable<AdvanceQuotaOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AdvanceQuotaOption[]>(
      '/CardQuota/getAdvanceQuotasSummaryForClient',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAdvanceQuotasSummaryForClient',
        token,
        headerOnly: false,
        params: { siteId, userId: this.currentUserId(), clientId },
      }
    );
  }

  getAdvanceVehicleTypesForClient(siteId: number, clientId: number): Observable<AdvanceVehicleTypeOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AdvanceVehicleTypeOption[]>(
      '/CardQuota/getAdvanceVehicleTypesForClient',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAdvanceVehicleTypesForClient',
        token,
        headerOnly: false,
        params: { siteId, userId: this.currentUserId(), clientId },
      }
    );
  }

  getAdvanceQuotasForClient(
    siteId: number,
    clientId: number,
    vehicleTypeId: number,
    page = 1,
    pageSize = 20,
    searchText?: string
  ): Observable<AdvanceQuotaOptionTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      clientId,
      vehicleTypeId,
      page,
      pageSize,
    };
    if (searchText?.trim()) params['searchText'] = searchText.trim();
    return this.apiMethodService.request<AdvanceQuotaOptionTableDto>(
      '/CardQuota/getAdvanceQuotasForClient',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getAdvanceQuotasForClient', token, headerOnly: false, params }
    );
  }

  getQuotaCardAdvanceActivateCodes(advanceId: number): Observable<QuotaCardAdvanceActivateCode[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<QuotaCardAdvanceActivateCode[]>(
      '/CardQuota/getQuotaCardAdvanceActivateCodes',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getQuotaCardAdvanceActivateCodes',
        token,
        headerOnly: false,
        params: { advanceId, userId: this.currentUserId() },
      }
    );
  }

  getUserClientAccessForAdmin(resUserId: number, siteId: number): Observable<AccessibleClientsForUserDto & { clientIds?: number[]; ClientIds?: number[] }> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/CardQuota/getUserClientAccessForAdmin',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getUserClientAccessForAdmin', token, headerOnly: false, params: { resUserId, siteId } }
    );
  }

  saveUserClientAccess(payload: {
    resUserId: number;
    siteId: number;
    userId: number;
    menuId: number;
    clientIds: number[];
  }): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/saveUserClientAccess',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'saveUserClientAccess', token, headerOnly: false, body: payload }
    );
  }

  saveQuotaCardAdvance(payload: SaveQuotaCardAdvancePayload): Observable<SaveQuotaCardAdvanceResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<SaveQuotaCardAdvanceResponse>(
      '/CardQuota/saveQuotaCardAdvance',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'saveQuotaCardAdvance', token, headerOnly: false, body: payload }
    );
  }

  executeQuotaCardAdvance(payload: QuotaCardAdvanceActionPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/executeQuotaCardAdvance',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'executeQuotaCardAdvance', token, headerOnly: false, body: payload }
    );
  }

  getCardAdvancesForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    clientId?: number | null,
    status?: number | null,
    dateFrom?: string | null,
    dateTo?: string | null
  ): Observable<CardAdvanceTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId,
      userId: this.currentUserId(),
      page,
      pageSize,
    };
    if (clientId != null) params['clientId'] = clientId;
    if (status != null) params['status'] = status;
    if (dateFrom) params['dateFrom'] = dateFrom;
    if (dateTo) params['dateTo'] = dateTo;
    return this.apiMethodService.request(
      '/CardQuota/getCardAdvancesForTable',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getCardAdvancesForTable', token, headerOnly: false, params }
    );
  }

  getCardAdvanceById(id: number): Observable<CardAdvanceDetail> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/CardQuota/getCardAdvanceById',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getCardAdvanceById', token, headerOnly: false, params: { id, userId: this.currentUserId() } }
    );
  }

  getStandardAdvanceVehicleTypes(siteId: number): Observable<AdvanceVehicleTypeOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AdvanceVehicleTypeOption[]>(
      '/CardQuota/getStandardAdvanceVehicleTypes',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getStandardAdvanceVehicleTypes', token, headerOnly: false, params: { siteId } }
    );
  }

  getStandardAdvanceConfigOptions(
    siteId: number,
    vehicleTypeId?: number,
    userId?: number
  ): Observable<StandardAdvanceConfigOption[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId };
    if (vehicleTypeId != null && vehicleTypeId > 0) {
      params['vehicleTypeId'] = vehicleTypeId;
    }
    const resolvedUserId = userId ?? this.currentUserId();
    if (resolvedUserId > 0) {
      params['userId'] = resolvedUserId;
    }
    return this.apiMethodService.request<StandardAdvanceConfigOption[]>(
      '/CardQuota/getStandardAdvanceConfigOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getStandardAdvanceConfigOptions',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getStandardAdvanceCardConfig(
    siteId: number,
    vehicleTypeId: number,
    subscriptionId?: number
  ): Observable<StandardAdvanceCardConfig> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId, vehicleTypeId };
    if (subscriptionId != null && subscriptionId > 0) {
      params['subscriptionId'] = subscriptionId;
    }
    return this.apiMethodService.request(
      '/CardQuota/getStandardAdvanceCardConfig',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getStandardAdvanceCardConfig',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  searchFinanceForCardAdvance(
    siteId: number,
    receiptId: string
  ): Observable<FinanceForCardAdvanceSearch[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/CardQuota/searchFinanceForCardAdvance',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'searchFinanceForCardAdvance',
        token,
        headerOnly: false,
        params: { siteId, receiptId: receiptId.trim() },
      }
    );
  }

  saveCardAdvance(payload: SaveCardAdvancePayload): Observable<SaveCardAdvanceResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/CardQuota/saveCardAdvance',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'saveCardAdvance', token, headerOnly: false, body: payload }
    );
  }

  executeCardAdvance(payload: CardAdvanceActionPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CardQuota/executeCardAdvance',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'executeCardAdvance', token, headerOnly: false, body: payload }
    );
  }
}
